import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import ContentEntryDetail from './content_entry_detail';
import { uploadUserImage } from '../../services/user_generated_content';
import {
  useUpdateContentEntryMutation,
  useUpdateSharedContentBoardMutation,
  useUpdateSharedContentEntryMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  contentEntryWithSectionUpdated,
  sharedContentEntryWithSectionUpdated,
} from '../../services/content_entries';
import {
  useGetContentEntryByIDAndParentQuery,
  useGetOrCreateSpaceContentBoardsQuery,
  useGetSharedContentEntryByIDAndParentQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { parentOfSharedContentEntryID } from '../../services/space_content_board';
import { touchResource } from '../../operations/utils';

const ContentEntryDetailContainer = ({
  parentSmorgBoardId,
  contentEntryID,
  appLinkEnabled,
  visible,
  dismiss,
}) => {
  // Must not access state from within callbacks passed to ContentEditable.
  // The issue is described at:
  // https://github.com/lovasoa/react-contenteditable/issues/161
  // We no longer use ContentEditable so this can be removed.
  const currentTextRef = useRef();
  const currentContentEntryRef = useRef();

  const [updateContentEntry] = useUpdateContentEntryMutation();

  const [updateSharedContentEntry] = useUpdateSharedContentEntryMutation();

  const [updateSharedContentBoard] = useUpdateSharedContentBoardMutation();

  const [currentlyEditingInline, setCurrentlyEditingInline] = useState({
    section: null,
    itemId: null,
  });

  const { data: sharedContentBoards } = useGetOrCreateSpaceContentBoardsQuery();

  const { data: ownContentEntry } = useGetContentEntryByIDAndParentQuery(
    { id: contentEntryID, parentID: parentSmorgBoardId },
    { skip: !contentEntryID || !parentSmorgBoardId },
  );

  const parentContentBoard = parentOfSharedContentEntryID(
    contentEntryID,
    sharedContentBoards,
  );

  const parentIDOfSharedContentEntry = parentContentBoard?.id;

  const updateSharedContentEntryAndParent = async (updatedEntry) => {
    const updatedParent = touchResource(parentContentBoard);
    await Promise.all([
      updateSharedContentEntry(updatedEntry),
      updateSharedContentBoard(updatedParent),
    ]);
  };

  const { data: sharedContentEntry } =
    useGetSharedContentEntryByIDAndParentQuery(
      { id: contentEntryID, parentID: parentIDOfSharedContentEntry },
      { skip: !contentEntryID || !parentIDOfSharedContentEntry },
    );

  const contentEntry = ownContentEntry || sharedContentEntry;

  const isSharedContentEntry = !!sharedContentEntry;

  useEffect(() => {
    currentContentEntryRef.current = contentEntry;
  }, [contentEntry]);

  const dismissEditingInline = () => {
    currentTextRef.current = null;
    setCurrentlyEditingInline({ section: null, itemId: null });
  };

  if (!visible || !contentEntry) {
    return null;
  }

  console.log(`ContentEntryDetail contentEntryID=${contentEntryID}`);

  const onInlineEditBlur = async (section, itemId, reason) => {
    console.log(`Blur ${section} ${itemId} ${reason}`);
    if (['title', 'body'].includes(section)) {
      const contentChanged =
        currentTextRef.current !== currentContentEntryRef.current[section];
      if (contentChanged) {
        const newContent = currentTextRef.current;
        if (section === 'body' && !newContent) {
          console.warn('newContent is blank! doing nothing.');
        } else if (isSharedContentEntry) {
          const updatedSharedContentEntry =
            sharedContentEntryWithSectionUpdated(
              sharedContentEntry,
              section,
              newContent,
            );
          await updateSharedContentEntryAndParent(updatedSharedContentEntry);
        } else {
          const updatedContentEntry = contentEntryWithSectionUpdated(
            contentEntry,
            section,
            newContent,
          );
          await updateContentEntry(updatedContentEntry);
        }
      }
    }

    dismissEditingInline();
  };

  const onInlineEditFocus = (section) => {
    console.log(`Focus ${section}`);
    const value = currentContentEntryRef.current[section] || '';
    currentTextRef.current = value.toString();
    setCurrentlyEditingInline({ section });
  };

  const onInlineEditChange = (section, itemId, value) => {
    console.log(`Change ${section}`);
    currentTextRef.current = value;
  };

  const onDialogClose = (ev, reason) => {
    if (reason === 'escapeKeyDown') {
      console.log('Dialog escape key down');
      // Prevent dialog from closing, let the contenteditable handle the esc key
      return null;
    }
    console.log(`Dialog close other reason: ${reason}`);
    dismiss();
    return true;
  };

  const onNewCoverImageChosen = async (fileList) => {
    const newImageUrl = await uploadUserImage(contentEntryID, fileList);
    if (isSharedContentEntry) {
      const updatedSharedContentEntry = {
        ...sharedContentEntry,
        coverImageUrl: newImageUrl,
      };
      await updateSharedContentEntryAndParent(updatedSharedContentEntry);
    } else {
      const updatedContentEntry = {
        ...contentEntry,
        coverImageUrl: newImageUrl,
      };
      await updateContentEntry(updatedContentEntry);
    }
  };

  const onAppLinkChange = async (newAppLink) => {
    if (isSharedContentEntry) {
      const updatedSharedContentEntry = {
        ...sharedContentEntry,
        appLink: newAppLink,
      };
      await updateSharedContentEntryAndParent(updatedSharedContentEntry);
    } else {
      const updatedContentEntry = {
        ...contentEntry,
        appLink: newAppLink,
      };
      await updateContentEntry(updatedContentEntry);
    }
  };

  const onCoverImageShownInDetailViewChange = async (
    newCoverImageShownInDetailView,
  ) => {
    if (isSharedContentEntry) {
      const updatedSharedContentEntry = {
        ...sharedContentEntry,
        isCoverImageShownInDetailView: newCoverImageShownInDetailView,
      };
      await updateSharedContentEntryAndParent(updatedSharedContentEntry);
    } else {
      const updatedContentEntry = {
        ...contentEntry,
        isCoverImageShownInDetailView: newCoverImageShownInDetailView,
      };
      await updateContentEntry(updatedContentEntry);
    }
  };

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      // Prevents TinyMCE issue - disabled inputs in Image dialogs invoked from toolbar
      disableEnforceFocus
      onClose={onDialogClose}>
      <ContentEntryDetail
        contentEntry={contentEntry}
        currentlyEditingInline={currentlyEditingInline}
        onInlineEditFocus={onInlineEditFocus}
        onInlineEditChange={onInlineEditChange}
        onInlineEditBlur={onInlineEditBlur}
        onNewCoverImageChosen={onNewCoverImageChosen}
        onDialogClose={onDialogClose}
        appLinkEnabled={appLinkEnabled}
        onAppLinkChange={onAppLinkChange}
        onCoverImageShownInDetailViewChange={
          onCoverImageShownInDetailViewChange
        }
      />
    </Dialog>
  );
};

ContentEntryDetailContainer.propTypes = {
  parentSmorgBoardId: PropTypes.string,
  contentEntryID: PropTypes.string,
  appLinkEnabled: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
  dismiss: PropTypes.func,
};

ContentEntryDetailContainer.defaultProps = {
  parentSmorgBoardId: null,
  contentEntryID: null,
  appLinkEnabled: false,
  dismiss: () => {},
};

export default ContentEntryDetailContainer;
